import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_ROSTER_PAGE_SIZE,
  PASSENGER_SORT_FIELDS,
  type PassengerDto,
  type PassengerListPage,
  type PassengerSortField,
  type RosterRoleFilter,
} from "@tc/shared";
import {
  Avatar,
  Button,
  Card,
  DocChip,
  Icon,
  Modal,
  Pagination,
  Segmented,
  SortHeader,
  TextInput,
  Toast,
} from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { useClubFilter } from "@/lib/clubs/club-filter";
import { useFormatters } from "@/lib/i18n/format";
import { rosterApi } from "./api";
import { READINESS } from "./labels";
import { PassengerForm } from "./PassengerForm";

/**
 * Roster surface: a club user manages its own roster; back office manages any club's roster on its
 * behalf once a specific club is picked in the topbar filter ("all clubs" stays read-only — a
 * passenger cannot be created without a club).
 */
export function RosterPage() {
  const { isBackOffice } = useAuthz();
  return isBackOffice ? <BackOfficeRoster /> : <EditableRoster />;
}

function readSort(value: string | null): PassengerSortField | undefined {
  return value && (PASSENGER_SORT_FIELDS as readonly string[]).includes(value)
    ? (value as PassengerSortField)
    : undefined;
}

function readRole(value: string | null): RosterRoleFilter {
  return value === "player" || value === "staff" ? value : "all";
}

/**
 * URL-driven, server-paginated roster query (page/sort/dir/role/q in the search params), scoped to
 * an optional club. `enabled` skips fetching (back-office "all clubs" shows a pick-a-club prompt
 * instead). Returns the current page plus the handlers the table wires its controls to. Sorting,
 * the player/staff filter and name search all run on the server, so they span every page.
 */
function useRosterQuery(clubId: string | undefined, enabled: boolean) {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const sort = readSort(params.get("sort"));
  const dir: "asc" | "desc" = params.get("dir") === "desc" ? "desc" : "asc";
  const role = readRole(params.get("role"));
  const q = params.get("q") ?? "";

  const [data, setData] = useState<PassengerListPage | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setData(
        await rosterApi.listPage({
          clubId,
          page,
          pageSize: DEFAULT_ROSTER_PAGE_SIZE,
          sort,
          dir,
          role,
          q: q || undefined,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [enabled, clubId, page, sort, dir, role, q]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function patch(mutate: (next: URLSearchParams) => void, resetPage = true) {
    const next = new URLSearchParams(params);
    mutate(next);
    if (resetPage) next.delete("page");
    setParams(next);
  }

  return {
    page,
    sort,
    dir,
    role,
    q,
    data,
    loading,
    refresh,
    onSort: (field: string) =>
      patch((next) => {
        if (sort === field) {
          next.set("sort", field);
          next.set("dir", dir === "asc" ? "desc" : "asc");
        } else {
          next.set("sort", field);
          next.set("dir", "asc");
        }
      }),
    onRole: (value: RosterRoleFilter) =>
      patch((next) => (value === "all" ? next.delete("role") : next.set("role", value))),
    onSearch: (value: string) => patch((next) => (value ? next.set("q", value) : next.delete("q"))),
    onPage: (p: number) => patch((next) => (p <= 1 ? next.delete("page") : next.set("page", String(p))), false),
  };
}

type RosterQuery = ReturnType<typeof useRosterQuery>;

/**
 * A roster with full CRUD (players + staff, document records, optional scans). With no `clubId`
 * it is the club user's own roster (their club is implied server-side); back office passes the
 * club selected in the filter so creates land in the right tenant.
 */
function EditableRoster({ clubId }: { clubId?: string }) {
  const { t } = useTranslation();
  const roster = useRosterQuery(clubId, true);
  const [editing, setEditing] = useState<PassengerDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<PassengerDto | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }, []);

  return (
    <>
      <RosterView roster={roster} onAdd={() => setCreating(true)} onEdit={setEditing} onDelete={setDeleting} />

      {(creating || editing) && (
        <PassengerForm
          passenger={editing}
          clubId={clubId}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            const wasCreating = creating;
            setCreating(false);
            setEditing(null);
            await roster.refresh();
            flash(t(wasCreating ? "roster.added" : "roster.saved"));
          }}
        />
      )}

      {deleting && (
        <ConfirmDelete
          passenger={deleting}
          onClose={() => setDeleting(null)}
          onConfirmed={async () => {
            setDeleting(null);
            await roster.refresh();
            flash(t("roster.removed"));
          }}
        />
      )}

      {toast && <Toast message={toast} />}
    </>
  );
}

/**
 * Back-office view: pick a club in the topbar switcher to manage its roster on the club's behalf
 * (full CRUD, TRAV-2). With "all clubs" selected there is no club to create into, so the page
 * stays a pick-a-club prompt. `key` remounts the editable view when the filter changes, so any
 * open modal never carries over to another club.
 */
function BackOfficeRoster() {
  const { t } = useTranslation();
  const { scopeId, isAll } = useClubFilter();

  if (isAll || !scopeId) {
    return (
      <div className="page">
        <div className="page-head">
          <h1>{t("roster.title")}</h1>
          <div className="sub">{t("roster.sub")}</div>
        </div>
        <div className="empty">
          <div className="empty-ic">
            <Icon name="roster" size={24} />
          </div>
          {t("roster.pickClub")}
        </div>
      </div>
    );
  }

  return <EditableRoster key={scopeId} clubId={scopeId} />;
}

interface RosterViewProps {
  roster: RosterQuery;
  readOnly?: boolean;
  onAdd?: () => void;
  onEdit?: (p: PassengerDto) => void;
  onDelete?: (p: PassengerDto) => void;
}

/** The roster table + filters, shared by the club (editable) and back-office (read-only) views. */
function RosterView({ roster, readOnly, onAdd, onEdit, onDelete }: RosterViewProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { data, loading, sort, dir, role, q } = roster;

  // Local search box value, debounced into the URL/query so search doesn't refetch on every keystroke.
  const [qInput, setQInput] = useState(q);
  useEffect(() => {
    setQInput(q);
  }, [q]);
  // Debounce on the input value only; onSearch/q are intentionally excluded so typing doesn't refetch.
  useEffect(() => {
    const id = setTimeout(() => {
      if (qInput !== q) roster.onSearch(qInput.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [qInput]);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? DEFAULT_ROSTER_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (roster.page - 1) * pageSize + 1;
  const to = Math.min(roster.page * pageSize, total);
  const filtered = role !== "all" || q.length > 0;

  return (
    <div className="page">
      <div className="row-between page-head">
        <div>
          <h1>{t("roster.title")}</h1>
          <div className="sub">{t("roster.sub")}</div>
        </div>
        {!readOnly && onAdd && (
          <Button icon="plus" onClick={onAdd}>
            {t("roster.addPassenger")}
          </Button>
        )}
      </div>

      <div className="row-between" style={{ marginBottom: 16, alignItems: "center" }}>
        <Segmented
          size="sm"
          value={role}
          onChange={roster.onRole}
          options={[
            { value: "all", label: t("roster.viewAll") },
            { value: "player", label: t("roster.player") },
            { value: "staff", label: t("roster.staff") },
          ]}
        />
        <div style={{ maxWidth: 260, width: "100%" }}>
          <TextInput value={qInput} onChange={setQInput} placeholder={t("roster.search")} icon="search" />
        </div>
      </div>

      {loading ? (
        <div className="empty">{t("common.comingSoon")}</div>
      ) : total === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="roster" size={24} />
          </div>
          {filtered ? t("roster.noMatches") : t("roster.empty")}
        </div>
      ) : (
        <>
          <Card pad={false}>
            <table className="table roster-table">
              <thead>
                <tr>
                  <SortHeader label={t("roster.name")} field="fullName" active={sort === "fullName"} dir={dir} onSort={roster.onSort} />
                  <th>{t("roster.role")}</th>
                  <SortHeader label={t("roster.nationality")} field="nationality" active={sort === "nationality"} dir={dir} onSort={roster.onSort} />
                  <th>{t("roster.docType")}</th>
                  <th>{t("roster.docNum")}</th>
                  <SortHeader label={t("roster.expiry")} field="expiry" active={sort === "expiry"} dir={dir} onSort={roster.onSort} />
                  <th>{t("roster.docStatus")}</th>
                  {!readOnly && <th aria-label="actions" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const readiness = READINESS[p.documentStatus];
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar initials={initials(p.fullName)} size={32} />
                          <b style={{ fontWeight: 600 }}>{p.fullName}</b>
                        </div>
                      </td>
                      <td>
                        {p.isStaff ? (
                          <span className="tag">{t("roster.staff")}</span>
                        ) : (
                          <span className="flag">{t("roster.player")}</span>
                        )}
                      </td>
                      <td className="mono muted">{p.nationality ?? "—"}</td>
                      <td>{p.documentType ?? "—"}</td>
                      <td className="mono muted" style={{ fontSize: 13 }}>
                        {p.documentNumber ?? "—"}
                      </td>
                      <td className="mono muted" style={{ fontSize: 13 }}>
                        {p.documentExpiry ? fmt.date(p.documentExpiry) : "—"}
                      </td>
                      <td>
                        <DocChip status={readiness.chip} icon={readiness.icon} label={t(readiness.key)} />
                      </td>
                      {!readOnly && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="admin-row-actions">
                            <Button variant="soft" size="sm" icon="edit" onClick={() => onEdit?.(p)}>
                              {t("actions.edit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon="x"
                              onClick={() => onDelete?.(p)}
                              aria-label={t("roster.remove")}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          <Pagination
            page={roster.page}
            pageCount={pageCount}
            onPage={roster.onPage}
            prevLabel={t("trips.pager.prev")}
            nextLabel={t("trips.pager.next")}
            summary={t("trips.pager.summary", { from, to, total })}
          />
        </>
      )}
    </div>
  );
}

/** Confirm soft-deleting a passenger (the row is hidden from the roster but never hard-deleted). */
function ConfirmDelete({
  passenger,
  onClose,
  onConfirmed,
}: {
  passenger: PassengerDto;
  onClose: () => void;
  onConfirmed: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await rosterApi.remove(passenger.id);
      await onConfirmed();
    } catch {
      setError(t("roster.removeError"));
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("roster.removeTitle")}>
      <div className="modal-body">
        {error && (
          <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
            <Icon name="alert" size={16} className="aa-ic" />
            <div className="aa-body">{error}</div>
          </div>
        )}
        <p>{t("roster.removeConfirm", { name: passenger.fullName })}</p>
      </div>
      <div className="modal-foot">
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          {t("actions.cancel")}
        </Button>
        <Button type="button" variant="danger" onClick={confirm} disabled={busy}>
          {t("roster.remove")}
        </Button>
      </div>
    </Modal>
  );
}

/** Up-to-two-letter initials for the avatar. */
function initials(name: string): string {
  return name
    .split(" ")
    .map((x) => x[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
